import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Mail, Phone, X, Briefcase, User, Info, ShieldCheck, ExternalLink, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchEpiscopalStaff } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';

// --- Improved Modal with Glassmorphism ---
const Modal = ({ isOpen, onClose, staff }) => {
  if (!staff) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden relative z-[101] flex flex-col md:flex-row max-h-[90vh]"
          >
            {/* Close Button - More prominent */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-[110] p-2 rounded-full bg-white/80 backdrop-blur-md text-slate-500 hover:bg-amber-100 hover:text-amber-700 shadow-sm transition-all border border-slate-100"
            >
              <X size={20} />
            </button>

            {/* Left Side: Profile & Identity */}
            <div className="w-full md:w-5/12 bg-slate-50 p-8 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-slate-100">
              <div className="relative group">
                <div className="absolute inset-0 bg-amber-600/10 rounded-[2rem] rotate-6 group-hover:rotate-0 transition-transform duration-500" />
                <div className="relative w-40 h-48 md:w-44 md:h-56 rounded-[2rem] overflow-hidden bg-white shadow-xl border-4 border-white">
                  {staff.photo ? (
                    <img src={staff.photo} alt={staff.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-300">
                      <User size={64} strokeWidth={1} />
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-full mb-3">
                  <ShieldCheck size={12} />
                  <span className="text-[10px] font-black uppercase tracking-wider">Verified Office</span>
                </div>
              </div>
            </div>

            {/* Right Side: Information Content */}
            <div className="w-full md:w-7/12 p-8 md:p-12 overflow-y-auto">
              <header className="mb-8">
                <h2 className="text-3xl font-serif font-bold text-slate-900 leading-tight mb-2">{staff.name}</h2>
                <div className="flex items-center gap-2">
                  <Briefcase size={14} className="text-amber-600" />
                  <p className="text-amber-600 font-bold uppercase tracking-[0.15em] text-xs">{staff.role}</p>
                </div>
              </header>

              <div className="space-y-8">
                {/* Brief Info */}
                <section>
                  <h4 className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-3 flex items-center gap-2">
                    <Info size={14} /> Brief Overview
                  </h4>
                  <p className="text-slate-600 leading-relaxed text-base font-serif">
                    {staff.brief_info || "This officer manages key administrative duties within the Episcopal office, ensuring the smooth running of diocesan affairs."}
                  </p>
                </section>

                {/* Responsibilities */}
                {staff.role_description && (
                  <section className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                    <h4 className="text-[10px] uppercase font-black text-slate-900 tracking-widest mb-3">Primary Mandates</h4>
                    <p className="text-slate-600 leading-relaxed text-sm font-serif italic">
                      {staff.role_description}
                    </p>
                  </section>
                )}

                {/* Quick Contact Footer */}
                <div className="pt-6 border-t border-slate-100 flex flex-wrap gap-4">
                  {staff.email && (
                    <a href={`mailto:${staff.email}`} className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-amber-600 transition-colors">
                      <Mail size={14} /> Email Office
                    </a>
                  )}
                  {staff.phone_number && (
                    <a href={`tel:${staff.phone_number}`} className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-amber-600 transition-colors">
                      <Phone size={14} /> Direct Line
                    </a>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// --- Staff Card with Interaction ---
const StaffCard = ({ staff, onClick }) => (
  <motion.div
    whileHover={{ y: -8 }}
    className="group relative"
    onClick={onClick}
  >
    <div className="absolute inset-0 bg-amber-600/5 rounded-[2.5rem] scale-95 opacity-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
    <Card className="border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer bg-white rounded-[2.5rem] overflow-hidden">
      <div className="p-8 flex flex-col items-center">
        <div className="relative mb-6">
          <div className="w-24 h-24 rounded-[2rem] overflow-hidden bg-slate-50 ring-8 ring-slate-50/50 transition-all group-hover:ring-amber-50 shadow-inner">
            {staff.photo ? (
              <img src={staff.photo} alt={staff.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                <User className="text-slate-400 w-10 h-10" />
              </div>
            )}
          </div>
          <div className="absolute -bottom-2 -right-2 bg-white p-2 rounded-xl shadow-lg border border-slate-50 opacity-0 group-hover:opacity-100 transition-opacity">
            <ExternalLink size={12} className="text-amber-600" />
          </div>
        </div>

        <div className="text-center space-y-1">
          <h3 className="text-lg font-serif font-bold text-slate-900 group-hover:text-amber-900 transition-colors line-clamp-1">{staff.name}</h3>
          <p className="text-amber-600 text-[10px] font-black uppercase tracking-widest">{staff.role}</p>
        </div>

        <div className="mt-6 flex gap-3">
          {staff.email && (
            <button className="p-2 rounded-full bg-slate-50 text-slate-400 hover:bg-amber-600 hover:text-white transition-all">
              <Mail size={14} />
            </button>
          )}
          {staff.phone_number && (
            <button className="p-2 rounded-full bg-slate-50 text-slate-400 hover:bg-amber-600 hover:text-white transition-all">
              <Phone size={14} />
            </button>
          )}
        </div>
      </div>
    </Card>
  </motion.div>
);

const EpiscopalOffice = () => {
  const [loading, setLoading] = useState(true);
  const [episcopalStaff, setEpiscopalStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const response = await fetchEpiscopalStaff();
        const data = response.data || [];
        setEpiscopalStaff(data.sort((a, b) => (a.hierarchy_level || 0) - (b.hierarchy_level || 0)));
      } catch (error) {
        toast({ title: "Network Update", description: "Displaying cached office personnel records." });
      } finally {
        setLoading(false);
      }
    };
    loadStaff();
  }, [toast]);

  const handleCardClick = (staff) => {
    setSelectedStaff(staff);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fcfaf7] flex items-center justify-center">
        <div className="relative flex items-center justify-center">
          <div className="h-16 w-16 border-2 border-amber-600/10 border-t-amber-600 rounded-full animate-spin" />
          <Briefcase className="absolute text-amber-600/20" size={20} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-24">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header Section */}
        <header className="text-center mb-24 relative">
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-3 px-5 py-2 bg-slate-900 rounded-full shadow-2xl mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-black text-white uppercase tracking-[0.3em]">Official Secretariat Active</span>
          </motion.div>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-slate-900 tracking-tight">
            The Episcopal <span className="text-amber-600 italic">Office</span>
          </h1>
          <p className="mt-6 text-slate-500 font-serif text-xl italic max-w-2xl mx-auto">
            Governing the Diocese of Ibadan South with integrity, vision, and spiritual stewardship.
          </p>
        </header>

        {/* Primary Leadership Tier */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-32">
          <LeaderCard
            title="The Lord Bishop"
            name="Rt. Rev. AkinTunde Popoola"
            quote="Leading with a vision for digital transformation and spiritual depth."
            accent="amber"
          />
          <LeaderCard
            title="President, Mother's Union"
            name="Dr. Oyebola Popoola"
            quote="Empowering families and nurturing the spiritual growth of our women."
            accent="purple"
          />
        </section>

        {/* Personnel Tier */}
        <section className="space-y-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 border-b border-slate-200 pb-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-600 rounded-2xl shadow-lg shadow-amber-200">
                <Briefcase className="text-white h-5 w-5" />
              </div>
              <div>
                <h2 className="text-3xl font-serif font-bold text-slate-900">Diocesan Staff</h2>
                <p className="text-xs text-slate-400 uppercase font-black tracking-widest mt-1">Administrative & Clerical Officers</p>
              </div>
            </div>
            <div className="px-6 py-3 bg-white rounded-2xl border border-slate-100 text-xs font-bold text-slate-500 italic">
              Total Personnel: {episcopalStaff.length}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {episcopalStaff.map((staff, index) => (
              <StaffCard key={index} staff={staff} onClick={() => handleCardClick(staff)} />
            ))}
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} staff={selectedStaff} />
    </div>
  );
};

const LeaderCard = ({ title, name, quote, accent }) => {
  const isAmber = accent === 'amber';
  return (
    <motion.div whileHover={{ scale: 1.01 }} className="relative group">
      <div className={`absolute inset-0 ${isAmber ? 'bg-amber-600' : 'bg-slate-900'} rounded-[3.5rem] translate-y-2 opacity-10 group-hover:opacity-20 transition-all`} />
      <Card className="border-none shadow-xl bg-white rounded-[3.5rem] p-12 relative overflow-hidden h-full">
        <div className={`absolute top-0 right-0 w-40 h-40 ${isAmber ? 'bg-amber-50' : 'bg-slate-50'} rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110`} />

        <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
          <div className={`w-32 h-32 shrink-0 rounded-[2.5rem] flex items-center justify-center ${isAmber ? 'bg-amber-600 shadow-amber-200' : 'bg-slate-900 shadow-slate-200'} shadow-2xl overflow-hidden`}>
            <User className="text-white w-12 h-12 opacity-20 absolute" />
            <div className="w-full h-full bg-gradient-to-tr from-black/20 to-transparent" />
          </div>

          <div className="text-center md:text-left space-y-4">
            <div>
              <p className={`text-[10px] font-black uppercase tracking-[0.4em] mb-2 ${isAmber ? 'text-amber-600' : 'text-slate-500'}`}>{title}</p>
              <h3 className="text-3xl font-serif font-bold text-slate-900">{name}</h3>
            </div>
            <p className="text-slate-500 font-serif italic text-lg leading-relaxed">"{quote}"</p>
            <div className={`h-1 w-12 rounded-full ${isAmber ? 'bg-amber-500' : 'bg-slate-900'}`} />
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default EpiscopalOffice;